/**
 * <device-check>
 *
 * Standalone IMEI device-compatibility check, extracted from
 * product-device-gate.js's format-validation/fetch/fail-closed core (see
 * docs/plans/phone-plp-plan.md D2). No province, no E911, no cart/form
 * gating — this element only ever reports one of four outcomes and,
 * on SUPPORTED, writes the shared sessionStorage carry-forward keys.
 *
 * Trigger model: explicit "Check your phone" button click, not
 * debounce-as-you-type — matches the real Figma pull (single CTA, no
 * separate nav link on this card), confirmed with operator 2026-08-24.
 *
 * Fail-closed: any non-200, timeout, or network error collapses to the
 * same "outage" (unavailable) status as a NOT_SUPPORTED-adjacent result —
 * never a silent pass. A malformed IMEI is rejected before any network
 * call fires.
 */
if (!customElements.get('device-check')) {
  customElements.define('device-check', class DeviceCheck extends HTMLElement {
    constructor() {
      super();

      this.apiBase = (this.dataset.apiBase || '').replace(/\/+$/, '');
      this.input = this.querySelector('input[name="imei"]');
      this.button = this.querySelector('[data-device-check-trigger]');
      this.status = this.querySelector('.device-check__status');
      this.statusText = this.querySelector('[data-device-check-status-text]');
      this.reassurance = this.querySelector('[data-device-check-reassurance]');
      this.strings = window.deviceCheckStrings || {};
      // Plan PDP D5 (Figma node-ids 73:28415/28607/28799/28991): none of
      // those frames show a CTA button — the check runs automatically once
      // a complete 15-digit IMEI is entered. The BYOP band's own confirmed
      // design (Figma node 78:22917, "a single button is the sole trigger,
      // no live debounce-as-you-type, confirmed with operator") is
      // untouched — this only activates when device-check.liquid renders
      // with auto_check: true and therefore no button.
      this.autoCheck = this.hasAttribute('data-auto-check');
      // docs/plans/imei-province-e911-plan.md S4 — only ever set on the
      // Plan PDP's instance (see device-check.liquid); absent everywhere
      // else, including the BYOP band, which never gates on this outcome
      // and always shows the soft notice regardless.
      // Rana Haddad (Pavocom), 9 Sep 2026, in writing: with the Telus
      // workaround in place device-check returns SUPPORTED when the device
      // is approved and UNKNOWN when it is not. UNKNOWN is therefore a
      // negative answer, not a missing one, so the default flipped from
      // 'continue' to 'block' — a phone that can't be approved also can't
      // be activated, and selling it a plan only produces a refund.
      // Verified the same day: 354379777756404 -> SUPPORTED,
      // 356729100581846 and 353322079360104 -> UNKNOWN.
      this.unknownResult = this.dataset.unknownResult || 'block';

      // The IMEI currently being checked over the network, and its
      // controller, so a second trigger for the same number reuses the
      // request in flight instead of firing a duplicate (see runDeviceCheck).
      this._inFlightImei = null;
      this._inFlightController = null;

      if (this.button) {
        this.button.addEventListener('click', this.onCheckClick.bind(this));
      }
      // "Try again" lives inside the status line after an outage outcome
      // (see applyOutcome). Delegated so the button can be re-rendered
      // freely with the message text.
      if (this.status) {
        this.status.addEventListener('click', (e) => {
          const retry = e.target.closest('[data-device-check-retry]');
          if (!retry) return;
          e.preventDefault();
          this.onRetryClick();
        });
      }
      if (this.input) {
        this.input.addEventListener('input', this.onInput.bind(this));
        if (this.autoCheck) {
          // Safety net for paste/autofill, which don't reliably fire a
          // per-keystroke `input` event the way typing does.
          this.input.addEventListener('blur', this.onAutoBlur.bind(this));
        }
      }

      // Carry-forward: a phone already confirmed elsewhere this session
      // (e.g. the phones PLP's BYOP band) prefills here rather than asking
      // again — but re-validates over the network rather than trusting the
      // carried value blindly (a province/network/store change since the
      // first check is possible, however unlikely). Was product-device-
      // gate.js's job on this same template before this build repointed
      // product.plan.json away from it; restored here so it isn't silently
      // lost for the products still relying on it.
      const carriedImei = this.getCarriedImei();
      if (carriedImei && this.input) {
        this.input.value = carriedImei;
        this.onCheckClick();
      }
    }

    // Rana's own test IMEIs arrive from labels and emails with spaces in
    // them; the activation form already strips non-digits before checking
    // (assets/speakout-activation.js), so this control did too little and
    // rejected a valid paste with a format error before any network call.
    // One normalizer, used everywhere a value is read.
    normalizeImei(raw) {
      return String(raw == null ? '' : raw).replace(/\D/g, '');
    }

    currentImei() {
      return this.normalizeImei(this.input ? this.input.value : '');
    }

    getCarriedImei() {
      try {
        return sessionStorage.getItem('speakout.imei');
      } catch (e) {
        return null;
      }
    }

    onInput() {
      // Typing clears a prior result — matches INTERACTIONS.md's "quiet
      // while typing" rule; the check itself only runs on button click
      // (or, in auto_check mode, once the IMEI is complete — see below).
      this.hideStatus();
      // Editing away from the number being checked cancels that check now,
      // rather than letting it complete and be thrown away by the
      // staleness guard in runDeviceCheck. Ztar was seeing those as calls
      // that "never arrived" (Rana, 9 Sep 2026); an explicit, immediate
      // cancel is both honest in the network log and less load on the BFF.
      if (this._inFlightController && this._inFlightImei !== this.currentImei()) {
        this._inFlightController.abort();
        this._inFlightImei = null;
        this._inFlightController = null;
      }
      if (this.autoCheck) this.maybeAutoCheck();
    }

    // auto_check mode only: runs the check the moment the field holds a
    // complete, well-formed 15-digit IMEI — no button to click. Format
    // validation inside onCheckClick still guards every other path (a
    // partial or malformed value never reaches the network).
    maybeAutoCheck() {
      if (/^[0-9]{15}$/.test(this.currentImei())) this.onCheckClick();
    }

    // Blur in auto_check mode: a complete IMEI runs the check
    // (paste/autofill safety net); a non-empty partial entry names the
    // problem instead of staying silent (8 Sep client feedback: typing 14
    // digits and leaving the field told the shopper nothing). Typing again
    // clears it via onInput's hideStatus.
    onAutoBlur() {
      const value = this.currentImei();
      if (!value) return;
      if (/^[0-9]{15}$/.test(value)) {
        // A complete IMEI has already been checked by maybeAutoCheck on the
        // keystroke that completed it. Re-running here fired a second,
        // identical request that Ztar saw as an abandoned call (Rana,
        // 9 Sep 2026). runDeviceCheck now dedupes, so this stays as the
        // paste/autofill safety net without duplicating live traffic.
        this.onCheckClick();
        return;
      }
      this.showStatus(this.strings.imeiFormatError || 'Enter a valid 15-digit IMEI.', 'error', 'error');
    }

    onCheckClick() {
      const value = this.currentImei();

      if (!/^[0-9]{15}$/.test(value)) {
        this.showStatus(this.strings.imeiFormatError || 'Enter a valid 15-digit IMEI.', 'error', 'error');
        if (this.input) this.input.focus();
        return;
      }

      // Per-visit cache: the same IMEI submitted twice in one session
      // re-shows the stored outcome instead of firing a second network
      // call. Single-slot (one active IMEI at a time), not a map — this
      // control has exactly one input, so there is never more than one
      // "current" IMEI to remember.
      const cachedImei = this.getCachedResult().imei;
      const cachedOutcome = this.getCachedResult().outcome;
      if (cachedImei === value && cachedOutcome) {
        this.applyOutcome(cachedOutcome, value);
        return;
      }

      if (!this.apiBase) {
        // No API base configured — fail closed, never assume compatibility.
        this.showStatus(this.strings.outageError || 'Check temporarily unavailable.', 'error', 'error');
        return;
      }

      this.runDeviceCheck(value);
    }

    getCachedResult() {
      try {
        return {
          imei: sessionStorage.getItem('speakout.device_check_imei'),
          outcome: sessionStorage.getItem('speakout.device_check_outcome'),
        };
      } catch (e) {
        return { imei: null, outcome: null };
      }
    }

    setCachedResult(imei, outcome) {
      try {
        sessionStorage.setItem('speakout.device_check_imei', imei);
        sessionStorage.setItem('speakout.device_check_outcome', outcome);
      } catch (e) {
        /* sessionStorage unavailable (private mode, etc.) — no cache for
           this visit, every submission re-checks; not a correctness issue,
           only a lost optimization. */
      }
    }

    // Renders one of the four distinguishable outcomes AND records it as
    // this.dataset.outcome + a bubbling CustomEvent, so a caller like the
    // Plan PDP's checkout gate can react to which outcome happened, not
    // just that "some" status was shown. `kind` drives icon+colour per
    // Figma (node-ids 73:28415/73:28607/73:28799/73:28991): supported gets
    // the checkmark; not_supported AND unknown ("IMEI not recognised") both
    // get the red X — same treatment, different copy; the network/API
    // failure alone gets the warning-circle icon, kept visually distinct
    // from the other two even though its text is the same red.
    applyOutcome(outcome, imei) {
      // Unknown's message depends on the admin setting (only ever set on
      // the Plan PDP instance — see the unknownResult comment above): the
      // gate that actually blocks add-to-cart on this outcome is
      // initPlanAddGate (assets/speakout-configurator.js), not here, so the
      // copy just needs to match what that gate decided.
      // 9 Sep 2026, Rana in writing: UNKNOWN now means "not on the
      // approved device list", not "we couldn't reach the check". The copy
      // says so plainly in both modes — the old "we couldn't confirm"
      // wording described an outage we are no longer having, and the
      // outage case has its own `error` outcome below.
      const unknownMessage =
        this.unknownResult === 'block'
          ? this.strings.unknownBlockedError || "This phone isn't on our approved device list, so we can't add a plan for it yet."
          : this.strings.unknownContinueNotice || "This phone isn't on our approved device list. You can continue, but it may not activate.";

      const messages = {
        not_supported: this.strings.notSupportedError || "This phone won't work on our network.",
        supported: this.strings.supportedMessage || 'Your phone works with our network',
        unknown: unknownMessage,
        error: this.strings.outageError || "We can't check your phone right now. Please try again in a moment.",
      }
      const kinds = {
        not_supported: 'error',
        supported: 'success',
        unknown: this.unknownResult === 'block' ? 'error' : 'warning',
        error: 'warning',
      }

      if (outcome === 'supported') {
        sessionStorage.setItem('speakout.imei', imei);
        sessionStorage.setItem('speakout.device_check', 'supported');
      }

      if (outcome === 'error') {
        // An outage is not a verdict on the phone. Offer a retry right in
        // the message so the shopper is never stranded waiting for the
        // API to wake up (Ztar's BFF was measured hanging 40s+ then
        // answering in 1.4s on 11 Sep 2026 — Marker SQU-9 / SQU-6).
        const retryLabel = this.strings.retryLabel || 'Try again';
        this.showStatus(
          `${messages.error} <button type="button" class="device-check__retry" data-device-check-retry>${retryLabel}</button>`,
          kinds.error,
          outcome
        );
        // Never cache an outage: the same IMEI re-entered must hit the
        // API again, otherwise one slow response locks the shopper out for
        // the rest of the session with zero requests visible.
        return;
      }

      this.showStatus(messages[outcome], kinds[outcome], outcome);
      this.setCachedResult(imei, outcome);
    }

    onRetryClick() {
      const imei = this.currentImei();
      if (!/^\d{15}$/.test(imei)) return;
      if (!this.apiBase) return;
      this._inFlightImei = null;
      this._inFlightController = null;
      this.runDeviceCheck(imei);
    }

    runDeviceCheck(imei) {
      // Ztar saw two calls per entry, one of them abandoned (Rana, 9 Sep
      // 2026): the keystroke completing the IMEI fired one and the
      // following blur fired an identical second. Same number already in
      // flight = nothing to do, the pending request will render.
      if (this._inFlightImei === imei) return;

      // A genuinely different number supersedes the old one. Aborting it
      // explicitly beats letting it land and be discarded by the staleness
      // guard below, which is what made an answered call look to Ztar like
      // a call that never arrived.
      if (this._inFlightController) this._inFlightController.abort();

      this.setBusy(true);
      this.showStatus(this.strings.checking || 'Checking…', 'checking');
      this._inFlightImei = imei;
      this.attemptDeviceCheck(imei, 0);
    }

    // One automatic retry before giving up. A failed check blocks the sale
    // (fail-closed, by design), so a single dropped request — a flaky
    // connection, a blocked cross-origin call, a cold BFF instance —
    // otherwise strands the shopper with no way forward but to re-edit the
    // field. Retrying once costs one request and rescues the common case.
    // Still fail-closed if both attempts fail.
    attemptDeviceCheck(requestImei, attempt) {
      const controller = new AbortController();
      // 45s. Was 8s, then 20s: the BFF runs on Cloud Run and a cold
      // instance was measured well past 8s (11 Sep 2026). Raised again on
      // Rana's request (Marker SQU-6/SQU-9, 11 Sep: "I can't control the
      // response time because it is external from telus so you need please
      // to increase the time limit from your end"). A slow answer is still
      // an answer; only a genuinely dead call should reach the outage copy.
      // Measured 14 Sep 2026: the endpoint now answers in ~1.1-1.4s, so
      // this ceiling only ever bites on a cold start.
      const timeoutId = setTimeout(() => controller.abort(), 45000);
      this._inFlightController = controller;

      const finish = () => {
        clearTimeout(timeoutId);
        if (this._inFlightImei === requestImei) {
          this._inFlightImei = null;
          this._inFlightController = null;
        }
        this.setBusy(false);
      };

      fetch(`${this.apiBase}/v1/ca/device-check?imei=${encodeURIComponent(requestImei)}`, {
        headers: { Accept: 'application/json' },
        signal: controller.signal,
      })
        .then((response) => {
          if (!response.ok) throw new Error(`device-check responded ${response.status}`);
          return response.json();
        })
        .then((data) => {
          clearTimeout(timeoutId);
          if (this.currentImei() !== requestImei) {
            finish();
            return;
          }

          // is_current_network_supported is the field the storefront has
          // always read; is_telus_supported is the one Telus' workaround
          // actually drives. Take either as a yes so an approved device is
          // never turned away because only one of the two came back set.
          const support = data && (data.is_current_network_supported || data.is_telus_supported);
          if (support === 'NOT_SUPPORTED') {
            this.applyOutcome('not_supported', requestImei);
          } else if (support === 'SUPPORTED') {
            this.applyOutcome('supported', requestImei);
          } else {
            // UNKNOWN, or any other value — since the Telus workaround this
            // means "not on the approved list", not "we couldn't ask".
            this.applyOutcome('unknown', requestImei);
          }
          finish();
        })
        .catch((err) => {
          clearTimeout(timeoutId);
          const superseded = this._inFlightImei !== requestImei;
          // A check we cancelled on purpose is not an outage — the newer
          // check renders its own result.
          if (err && err.name === 'AbortError' && superseded) return;
          if (superseded || this.currentImei() !== requestImei) {
            finish();
            return;
          }
          if (attempt === 0) {
            setTimeout(() => {
              // Only retry if this IMEI is still the one on screen and
              // nothing newer has taken over in the meantime.
              if (this._inFlightImei !== requestImei) return;
              if (this.currentImei() !== requestImei) {
                finish();
                return;
              }
              this.attemptDeviceCheck(requestImei, 1);
            }, 600);
            return;
          }
          this.applyOutcome('error', requestImei);
          finish();
        });
    }

    setBusy(busy) {
      if (!this.button) return;
      this.button.disabled = busy;
      this.button.setAttribute('aria-busy', busy ? 'true' : 'false');
    }

    // `outcome`, when given, is the machine-readable 4-way result
    // (supported/unknown/not_supported/error) recorded on the host element
    // and broadcast — distinct from `kind`, which drives icon+colour and
    // deliberately gives not_supported/unknown the same red-X treatment
    // while keeping the network-failure `error` outcome on its own
    // warning-circle icon (see applyOutcome). `message` is theme-authored
    // copy (locale JSON via window.deviceCheckStrings, or a static JS
    // fallback) — never user input — so innerHTML is safe here and is
    // what lets the not-supported message's "shop" link actually render
    // as a link instead of literal text.
    showStatus(message, kind, outcome) {
      if (!this.status || !message) return;
      if (this.statusText) this.statusText.innerHTML = message
      this.status.dataset.kind = kind;
      this.status.hidden = false;
      // Client feedback 8 Sep 2026: nothing shows before an IMEI outcome,
      // in every mode — the reassurance line ships hidden from Liquid and
      // is kept hidden here too in case older markup is still cached.
      if (this.reassurance) this.reassurance.hidden = true;
      if (outcome) {
        this.dataset.outcome = outcome;
        this.dispatchEvent(
          new CustomEvent('speakout:device-check:outcome', {
            bubbles: true,
            detail: { outcome },
          })
        );
      }
    }

    hideStatus() {
      if (!this.status) return;
      this.status.hidden = true;
      delete this.status.dataset.kind;
      delete this.dataset.outcome;
      // Reassurance stays hidden on reset as well (8 Sep client feedback):
      // an empty field shows no copy at all.
    }
  });
}
